import gql from 'graphql-tag';
import { MobXProviderContext } from 'mobx-react';
import React from 'react';
import PropTypes from 'prop-types';
import { updateUserAttribute, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AuthProvider as DescopeProvider } from '@descope/react-sdk';

import { isBranchEnv } from 'brand-config';
import awsConfig from 'aws-exports';
import { withToast } from 'core/components/toast';
import { CognitoPermissionGroups } from 'core/helpers/cognito-permission-groups';
import { initializeFrontChatAndIdentify, identifyUserInUpscope, identifyUserInAppcues } from 'configure-services';
import { AuthContext } from './auth.context';
import { identify, track } from 'core/helpers/analytics';
import oAuthStorage from 'core/components/auth/oauth-storage';

const isProductionEnv = import.meta.env.VITE_STACKERY_ENVIRONMENT === 'production';

function getPermissions(userAttributes) {
  const groups = userAttributes['cognito:groups'] || [];

  if (!groups.length) {
    return {
      isService: false,
      isSales: false,
      canEdit: false
    };
  }

  // licensed agent groups

  const isService = groups.includes('Service');
  const isSales = groups.includes('InternalSales') || groups.includes('ExternalSales');
  const isInternalSales = groups.includes('InternalSales');
  const isExternalSales = groups.includes('ExternalSales');
  const isTeamLeader = groups.includes('TeamLeaders');

  const parseCustomAttribute = (attr) => {
    try {
      return JSON.parse(attr);
    } catch {
      return [];
    }
  };

  // logic surrounding licensed and unlicensed states for external users
  const isAgency =
    !!parseCustomAttribute(userAttributes['custom:affinity_groups']).length ||
    !!parseCustomAttribute(userAttributes['custom:affinity_group_regex']).length;
  const parsedAllowedStates = parseCustomAttribute(userAttributes['custom:allowed_states']);
  const parsedUnlicensedStates = parseCustomAttribute(userAttributes['custom:unlicensed_states']);

  // individual features access
  const canReinstate = groups.includes('Reinstatement');
  const canRescindCancellation = groups.includes('RescindCancellation');
  const canSeeFullOfferDetails = groups.includes('CompleteOfferDetails');
  const canScrubIncidents = groups.includes('ScrubIncidents');
  const canModifyAffinityAndLeadSource = groups.includes('ModifyAffinityCodes');
  const canBackDate = groups.includes('CanBackDate');
  const canModifyBillingId = groups.includes('ModifyBillingID');
  const canViewClarionDoorData = groups.includes('ViewClarionDoorData');
  const canAddCarsManually = groups.includes('AddCarsManually');
  const canAutoRenew = groups.includes('CanAutoRenew');
  const canAddHoldCards = groups.includes('CanAddHoldCards');
  const canChangeExclusions = groups.includes('CanChangeExclusions');
  const hasUnlicensedGroup = groups.includes('Unlicensed');
  const canToggleEmployeeDiscount = groups.includes('CanToggleEmployeeDiscount');
  const canClearUDRs = groups.includes('CanClearUDRs');
  const canEditHomeAddress = groups.includes('CanEditHomeAddress');
  const canEditEffectiveDate = groups.includes('CanEditEffectiveDate');
  const canManuallyChargeFee = groups.includes('CanManuallyChargeFee');
  const canQuoteQuinstreet = groups.includes('CanQuoteQuinstreet');
  const canClearUWFormRejections = groups.includes('CanClearUWFormRejections');
  const canManuallyChargePolicy = groups.includes('CanManuallyChargePolicy');
  const canRewritePolicy = groups.includes('CanRewritePolicy');
  const canRemoveExcludedDriver = groups.includes('CanRemoveExcludedDriver');
  const canCancelReasonCNIP = groups.includes('CanCancelReasonCNIP');
  const canCancelReasonCNMS = groups.includes('CanCancelReasonCNMS');
  const canCancelReasonCNPY_X = groups.includes('CanCancelReasonCNPYX');
  const canRejectPIP = groups.includes('CanRejectPIP');
  const showAgencyPortal = groups.includes('ShowAgencyPortal');
  const hasCanQuoteGroup = groups.includes('CanQuote');
  const hasCanViewPolicyGroup = groups.includes('CanViewPolicy');
  const canViewQlikReports = groups.includes('CanViewQlikReports');
  const canDownloadAnonDocs = groups.includes('CanDownloadAnonDocs');
  const canReinstateCNPY = groups.includes('CanReinstateCNPY');
  const canToggleCreditCheck = groups.includes('CanToggleCreditCheck');
  const canEditBillingDay = groups.includes('CanEditBillingDay');
  const canToggleCHDiscount = groups.includes('CanToggleCHDiscount');
  const canToggleInventoryDiscount = groups.includes('CanToggleInventoryDiscount');
  const canEditPolicyMPD = groups.includes('CanEditPolicyMPD');
  const canDelayPaymentOrRefund = groups.includes('CanDelayPaymentOrRefund');
  const canManageBillingHolds = groups.includes('CanManageBillingHolds');
  const canSendPaperDocs = groups.includes('CanSendPaperDocs');
  const canEditFeeCheckbox = groups.includes('CanEditFeeCheckbox');
  const canOverrideGaragingAddress = groups.includes('CanOverrideGaragingAddress');
  const canSwapApplicants = groups.includes('CanSwapApplicants');
  const canViewCarrierInitiatedCancels = groups.includes('CanViewCarrierInitiatedCancels');
  const canSellAndService = groups.includes('ExternalSales') && parsedAllowedStates?.length > 0;
  const canCancelReasonNACP = groups?.includes('CanCancelReasonNACP');
  const canEditUneditablePropertyDetailsFields = groups?.includes('CanEditUneditablePropertyDetails');
  const canResetOnboardingTasks = groups?.includes('CanResetOnboardingTasks');
  const canViewUWCancelCodes = groups?.includes('CanViewUWCancelCodes');
  const canToggleIsAgencyPolicy = groups?.includes('CanToggleIsAgencyPolicy');
  const canRefundStripePayment = groups?.includes('CanRefundStripePayment');
  const canRescindOrReinstateGSNICPolicy = groups?.includes('CanRescindOrReinstateGSNICPolicy');
  const canRemoveFinalPaymentMethod = groups?.includes('CanRemoveFinalPaymentMethod');
  const canBypassDriversLicenseCheck = groups?.includes('CanBypassDriversLicenseCheck');
  const canEditCoverageA = groups?.includes('CanEditCoverageA');
  const canSeeMarketTierCard = groups?.includes('CanSeeMarketTierCard');
  const canWriteMultipleAffinityCodes = groups?.includes('CanWriteMultipleAffinityCodes');
  const canEditDriversName = groups?.includes('CanEditDriversName');
  const canModifyNonRatedIndividual = groups?.includes('CanModifyNonRatedIndividual');
  const canRevertPolicyDetails = groups?.includes('CanRevertPolicyDetails');
  const canToggleHomeRepairsRequested = groups?.includes('CanToggleHomeRepairsRequested');
  const canApproveRequestedPIPChanges = groups?.includes('CanApproveRequestedPIPChanges');
  const canAccessProtectedUDDActions = groups.includes('canAccessProtectedUDDActions');
  const canEditPriorCarrierDetails = groups.includes('CanEditPriorCarrierDetails');
  const canSkipOfferUnderwritingRules = groups.includes('CanSkipOfferUnderwritingRules');

  // can view both offers and policies but will not allow editing
  const viewOnly = groups.includes('ViewOnly');

  // Users that are not licensed in any states and therefore should only have a populated list of unlicensed states alongside the Unlicensed cognito group
  const isUnlicensed = hasUnlicensedGroup && Boolean(parsedUnlicensedStates?.length && !parsedAllowedStates.length);

  // Users that are not licensed in any states will have access to editing only unlicensed fields on offers/policies.
  // Alongside the Unlicensed group, they should also have populated list of unlicensed states.
  // The form field component logic takes care of disabling the licensed fields for these users.
  const canEditPolicies = (isTeamLeader || isSales || isService || isUnlicensed) && !viewOnly;
  const canEditOffers = (isTeamLeader || isSales || isService || isUnlicensed || hasCanQuoteGroup) && !viewOnly;
  const canViewOffers = viewOnly || canEditOffers;
  const canViewPolicies = viewOnly || hasCanViewPolicyGroup || canEditPolicies;

  // tools permissions:
  const canViewTools = groups.includes('CanViewTools');
  const canViewAffinityTools = groups.includes('toolsCanViewAffinityTools');
  const canViewStaffDashboard = groups.includes('toolsStaffDashboard');
  const canResetStaffUserPassword = groups.includes('toolsStaffDashboardResetPassword');
  const canDisableStaffUser = groups.includes('toolsStaffDashboardDisableUser');
  const toolsDeleteRenewalSegment = groups.includes('toolsDeleteRenewalSegment');
  const canCreateStripeCharge = groups.includes('toolsCreateStripeCharge');
  const canCreateChargedeskInvoice = groups.includes('toolsCreateChargedeskInvoice');
  const canCreateChargedeskRefundInvoice = groups.includes('toolsChargedeskRefundInvoice');
  const canBulkDisableStaffUsers = groups.includes('toolsStaffUsersBulkDisable');
  const canRunAddressesThroughCherre = groups.includes('toolsRunAddressesThroughCherre');
  const canRegeneratePolicyForm = groups.includes('toolsRegeneratePolicyForm');
  const canRegeneratePolicyCancelForm = groups.includes('toolsRegeneratePolicyCancelForm');
  const canUpdateChargedeskRecord = groups.includes('toolsChargedeskUpdateRecord');
  const canUpdateChargedeskRecordPolicyOrBillingId = groups.includes('toolsCDUpdateRecordLimited');
  const canGenerateRenewal = groups.includes('toolsCanGenerateRenewal');
  const canRunRenewalUnderwriting = groups.includes('toolsCanRunRenewalUnderwriting');
  const canUpdateStaffUser = groups.includes('toolsStaffDashboardUpdateUser');
  const canViewEditGroups = groups.includes('toolsViewEditUserGroups');
  const canUpdatePolicyRep = groups.includes('toolsUpdatePolicyRep');
  const canAddPermissionGroups = groups.includes('toolsAddPermissionGroups');
  const canCreateStaffUser = groups.includes('toolsCreateStaffUser');
  const canCreateFrontTicket = groups.includes('toolsCreateFrontTicket');
  const canViewStateRateChangeDashboard = groups.includes('toolsStateRateChangeDashboard');
  const canImportPoliciesToSnapsheet = groups.includes('toolsImportPoliciesToSnapsheet');
  const canImportPoliciesToIvans = groups.includes('toolsImportPoliciesToIvans');
  const canGetIvansFormattedPolicy = groups.includes('toolsGetIvansFormattedPolicy');
  const canAddCatTagsToClaims = groups.includes('toolsCanAddCatTagsToClaims');
  const canRunTechTasks = groups.includes('toolsCanRunTechTasks');
  const canCopyPolicy = groups.includes('toolsCanCopyPolicy');
  const canSetMemberAffinity = groups.includes('toolsCanSetMemberAffinity');
  const canEnableStaffUser = groups.includes('toolsCanEnableStaffUser');
  const canResetPasskeys = groups.includes('toolsCanResetPasskeys');
  const canPullDrivingHistoryFromCSV = groups.includes('toolsCanPullDrivingHistory');
  const canSetHasAutoPolicy = groups.includes('toolsCanSetHasAutoPolicy');
  const canUpdateBillingId = groups.includes('toolsCanUpdateBillingId');
  const canOverrideUmbrellaLimit = groups.includes('toolsCanOverrideUmbrellaLimit');
  const canRunCorrectiveActions = groups.includes('toolsCanRunCorrective');
  const canChangeOfferAffinity = groups.includes('toolsCanChangeOfferAffinity');
  const canAddViolationToOffer = groups.includes('toolsAddAutoViolationsOffer');
  const canUpdateStaffUsername = groups.includes('toolsCanUpdateStaffUsername');
  const canUpdateMortgage = groups.includes('toolsCanEditMortgage');
  const canRunBillingActions = groups.includes('toolsBillingActions');
  const canRunIvansActions = groups.includes('toolsIvansActions');
  const canBackfillData = groups.includes('toolsCanBackfillData');
  const canCreateCompaniesInRoutable = groups.includes('toolsCanCreateCompaniesInRoutable');
  const canSkipMatchingActivePolicySearch = groups.includes('toolsCanSkipMatchingActivePolicySearch');
  const canBulkOrderInspections = groups.includes('toolsCanBulkOrderInspections');
  const canOverrideBrandedTitle = groups.includes('toolsCanOverrideBrandedTitle');
  const canOverrideExcludedCar = groups.includes('toolsCanOverrideExcludedCar');
  const canOverrideMSRP = groups.includes('toolsCanOverrideMSRP');
  const canOverrideLoanNumVal = groups.includes('toolsCanOverrideLoanNumVal');
  const canAllowAuxRating = groups.includes('toolsCanAllowAuxRating');
  const canBulkDownloadPolicyDocuments = groups.includes('toolsCanBulkDownloadPolicyDocuments');
  const canSeeHomeCharacteristicsDataset = groups.includes('toolsOperationsActions');
  const canAllowWaterBackup = groups.includes('toolsCanAllowWaterBackup');
  const canRefreshCharacteristicsAndRateOffer = groups.includes('toolsOperationsActions');
  const canRemoveAutoViolation = groups.includes('toolsRemoveAutoViolation');

  const canViewPolicyOverridesTool =
    canOverrideUmbrellaLimit || canOverrideBrandedTitle || canOverrideExcludedCar || canOverrideMSRP;
  const canViewOfferOverridesTool =
    canOverrideLoanNumVal || canAllowAuxRating || canAllowWaterBackup || canOverrideExcludedCar || canOverrideMSRP;

  return {
    // allows quoting, editing, and viewing offers
    [CognitoPermissionGroups.canQuote]:
      (isTeamLeader || isSales || isService || hasCanQuoteGroup || isUnlicensed) && !viewOnly,

    // allows purchasing offers/binding
    [CognitoPermissionGroups.canBind]: (isTeamLeader || isSales || isService) && !viewOnly,

    // allows viewing the checkout route but not necessarily binding
    [CognitoPermissionGroups.canViewCheckoutPage]:
      (isTeamLeader || isSales || isService || hasCanQuoteGroup) && !viewOnly,

    [CognitoPermissionGroups.canViewPolicies]: canViewPolicies,
    [CognitoPermissionGroups.canViewOffers]: canViewOffers,

    // if opening policy/offer for state not licensed for, the code should only allow unlicensed actions
    // which is determined via the permissions prop and useFormFieldPermissionHelpers used in FormField
    [CognitoPermissionGroups.canEditPolicies]: canEditPolicies,
    [CognitoPermissionGroups.canEditOffers]: canEditOffers,
    [CognitoPermissionGroups.canEdit]: canEditPolicies || canEditOffers,

    // allows viewing offers, policies, and customer, but does not allow editing, checkout, or modifying the data of any kind
    [CognitoPermissionGroups.viewOnly]: viewOnly,
    [CognitoPermissionGroups.isTeamLeader]: isTeamLeader && !viewOnly,
    [CognitoPermissionGroups.isSales]: isSales,
    [CognitoPermissionGroups.isService]: isService && !viewOnly,
    [CognitoPermissionGroups.isAgency]: isAgency,
    [CognitoPermissionGroups.isInternalAgent]: !isAgency,
    [CognitoPermissionGroups.canAccessProtectedUDDActions]: canAccessProtectedUDDActions,
    [CognitoPermissionGroups.isUnlicensed]: isUnlicensed,
    [CognitoPermissionGroups.canReinstate]: canReinstate,
    [CognitoPermissionGroups.canRescindCancellation]: canRescindCancellation,
    [CognitoPermissionGroups.canSeeFullOfferDetails]: canSeeFullOfferDetails,
    [CognitoPermissionGroups.isInternalSales]: isInternalSales && !viewOnly,
    [CognitoPermissionGroups.isExternalSales]: isExternalSales && !viewOnly,
    [CognitoPermissionGroups.canScrubIncidents]: canScrubIncidents,
    [CognitoPermissionGroups.canModifyAffinityAndLeadSource]: canModifyAffinityAndLeadSource,
    [CognitoPermissionGroups.canBackDate]: canBackDate,
    [CognitoPermissionGroups.canModifyBillingId]: canModifyBillingId && !isAgency,
    [CognitoPermissionGroups.canViewClarionDoorData]: canViewClarionDoorData,
    [CognitoPermissionGroups.canAddCarsManually]: canAddCarsManually && !isAgency,
    [CognitoPermissionGroups.canAutoRenew]: canAutoRenew && !isAgency,
    [CognitoPermissionGroups.canAddHoldCards]: canAddHoldCards,
    [CognitoPermissionGroups.canToggleEmployeeDiscount]: canToggleEmployeeDiscount && !isAgency,
    [CognitoPermissionGroups.canChangeExclusions]: canChangeExclusions,
    [CognitoPermissionGroups.canClearUDRs]: canClearUDRs,
    [CognitoPermissionGroups.canEditHomeAddress]: canEditHomeAddress,
    [CognitoPermissionGroups.canEditEffectiveDate]: canEditEffectiveDate,
    [CognitoPermissionGroups.canManuallyChargeFee]: canManuallyChargeFee,
    [CognitoPermissionGroups.canQuoteQuinstreet]: canQuoteQuinstreet,
    [CognitoPermissionGroups.canClearUWFormRejections]: canClearUWFormRejections,
    [CognitoPermissionGroups.canUploadDocuments]: (isInternalSales || isService || isTeamLeader) && !viewOnly,
    [CognitoPermissionGroups.canManuallyChargePolicy]: canManuallyChargePolicy,
    [CognitoPermissionGroups.canRewritePolicy]: canRewritePolicy,
    [CognitoPermissionGroups.canRemoveExcludedDriver]: canRemoveExcludedDriver,
    [CognitoPermissionGroups.canCancelReasonCNIP]: canCancelReasonCNIP,
    [CognitoPermissionGroups.canCancelReasonCNMS]: canCancelReasonCNMS,
    [CognitoPermissionGroups.canCancelReasonCNPY_X]: canCancelReasonCNPY_X,
    [CognitoPermissionGroups.canRejectPIP]: canRejectPIP,
    // Temporarily hiding agency portal for mutuals
    [CognitoPermissionGroups.showAgencyPortal]: showAgencyPortal || isAgency,
    [CognitoPermissionGroups.canViewQlikReports]: canViewQlikReports,
    [CognitoPermissionGroups.canDownloadAnonDocs]: canDownloadAnonDocs,
    [CognitoPermissionGroups.canReinstateCNPY]: canReinstateCNPY,
    [CognitoPermissionGroups.canToggleCreditCheck]: canToggleCreditCheck,
    [CognitoPermissionGroups.canEditBillingDay]: canEditBillingDay,
    [CognitoPermissionGroups.canToggleCHDiscount]: canToggleCHDiscount,
    [CognitoPermissionGroups.canToggleInventoryDiscount]: canToggleInventoryDiscount,
    [CognitoPermissionGroups.canEditPolicyMPD]: canEditPolicyMPD,
    [CognitoPermissionGroups.canDelayPaymentOrRefund]: canDelayPaymentOrRefund,
    [CognitoPermissionGroups.canManageBillingHolds]: canManageBillingHolds,
    [CognitoPermissionGroups.canSendPaperDocs]: canSendPaperDocs,
    [CognitoPermissionGroups.canEditFeeCheckbox]: canEditFeeCheckbox,
    [CognitoPermissionGroups.canOverrideGaragingAddress]: canOverrideGaragingAddress,
    [CognitoPermissionGroups.canSwapApplicants]: canSwapApplicants,
    [CognitoPermissionGroups.canViewCarrierInitiatedCancels]: canViewCarrierInitiatedCancels,
    [CognitoPermissionGroups.canSellAndService]: canSellAndService,
    [CognitoPermissionGroups.canCancelReasonNACP]: canCancelReasonNACP,
    [CognitoPermissionGroups.canEditUneditablePropertyDetailsFields]: canEditUneditablePropertyDetailsFields,
    [CognitoPermissionGroups.canResetOnboardingTasks]: canResetOnboardingTasks,
    [CognitoPermissionGroups.canViewUWCancelCodes]: canViewUWCancelCodes,
    [CognitoPermissionGroups.canToggleIsAgencyPolicy]: canToggleIsAgencyPolicy,
    [CognitoPermissionGroups.canRefundStripePayment]: canRefundStripePayment,
    [CognitoPermissionGroups.canViewTools]: canViewTools,
    [CognitoPermissionGroups.canViewAffinityTools]: canViewAffinityTools,
    [CognitoPermissionGroups.canViewStaffDashboard]: canViewStaffDashboard,
    [CognitoPermissionGroups.canResetStaffUserPassword]: canResetStaffUserPassword,
    [CognitoPermissionGroups.toolsDeleteRenewalSegment]: toolsDeleteRenewalSegment,
    [CognitoPermissionGroups.canCreateStripeCharge]: canCreateStripeCharge,
    [CognitoPermissionGroups.canCreateChargedeskInvoice]: canCreateChargedeskInvoice,
    [CognitoPermissionGroups.canBulkDisableStaffUsers]: canBulkDisableStaffUsers,
    [CognitoPermissionGroups.canCreateChargedeskRefundInvoice]: canCreateChargedeskRefundInvoice,
    [CognitoPermissionGroups.canRegeneratePolicyForm]: canRegeneratePolicyForm,
    [CognitoPermissionGroups.canUpdateChargedeskRecord]: canUpdateChargedeskRecord,
    [CognitoPermissionGroups.canUpdateChargedeskRecordPolicyOrBillingId]: canUpdateChargedeskRecordPolicyOrBillingId,
    [CognitoPermissionGroups.canGenerateRenewal]: canGenerateRenewal,
    [CognitoPermissionGroups.canRunRenewalUnderwriting]: canRunRenewalUnderwriting,
    [CognitoPermissionGroups.canUpdateStaffUser]: canUpdateStaffUser,
    [CognitoPermissionGroups.canDisableStaffUser]: canDisableStaffUser,
    [CognitoPermissionGroups.canViewEditGroups]: canViewEditGroups,
    [CognitoPermissionGroups.canRunAddressesThroughCherre]: canRunAddressesThroughCherre,
    [CognitoPermissionGroups.canUpdatePolicyRep]: canUpdatePolicyRep,
    [CognitoPermissionGroups.canAddPermissionGroupsToStaffUsers]: canAddPermissionGroups,
    [CognitoPermissionGroups.canCreateStaffUser]: canCreateStaffUser,
    [CognitoPermissionGroups.canCreateFrontTicket]: canCreateFrontTicket,
    [CognitoPermissionGroups.canViewStateRateChangeDashboard]: canViewStateRateChangeDashboard,
    [CognitoPermissionGroups.canImportPoliciesToSnapsheet]: canImportPoliciesToSnapsheet,
    [CognitoPermissionGroups.canImportPoliciesToIvans]: canImportPoliciesToIvans,
    [CognitoPermissionGroups.canGetIvansFormattedPolicy]: canGetIvansFormattedPolicy,
    [CognitoPermissionGroups.canRegeneratePolicyCancelForm]: canRegeneratePolicyCancelForm,
    [CognitoPermissionGroups.canAddCatTagsToClaims]: canAddCatTagsToClaims,
    [CognitoPermissionGroups.canRunTechTasks]: canRunTechTasks,
    [CognitoPermissionGroups.canRescindOrReinstateGSNICPolicy]: canRescindOrReinstateGSNICPolicy,
    [CognitoPermissionGroups.canRemoveFinalPaymentMethod]: canRemoveFinalPaymentMethod,
    [CognitoPermissionGroups.canCopyPolicy]: canCopyPolicy,
    [CognitoPermissionGroups.canSetMemberAffinity]: canSetMemberAffinity,
    [CognitoPermissionGroups.canEnableStaffUser]: canEnableStaffUser,
    [CognitoPermissionGroups.canResetPasskeys]: canResetPasskeys,
    [CognitoPermissionGroups.canPullDrivingHistoryFromCSV]: canPullDrivingHistoryFromCSV,
    [CognitoPermissionGroups.canSetHasAutoPolicy]: canSetHasAutoPolicy,
    [CognitoPermissionGroups.canUpdateBillingId]: canUpdateBillingId,
    [CognitoPermissionGroups.canOverrideUmbrellaLimit]: canOverrideUmbrellaLimit,
    [CognitoPermissionGroups.canOverrideBrandedTitle]: canOverrideBrandedTitle,
    [CognitoPermissionGroups.canOverrideMSRP]: canOverrideMSRP,
    [CognitoPermissionGroups.canOverrideExcludedCar]: canOverrideExcludedCar,
    [CognitoPermissionGroups.canBypassDriversLicenseCheck]: canBypassDriversLicenseCheck,
    [CognitoPermissionGroups.canRunCorrectiveActions]: canRunCorrectiveActions,
    [CognitoPermissionGroups.canChangeOfferAffinity]: canChangeOfferAffinity,
    [CognitoPermissionGroups.canAddViolationToOffer]: canAddViolationToOffer,
    [CognitoPermissionGroups.canUpdateStaffUsername]: canUpdateStaffUsername,
    [CognitoPermissionGroups.canEditCoverageA]: canEditCoverageA,
    [CognitoPermissionGroups.canSeeMarketTierCard]: canSeeMarketTierCard,
    [CognitoPermissionGroups.canWriteMultipleAffinityCodes]: canWriteMultipleAffinityCodes,
    [CognitoPermissionGroups.canEditDriversName]: canEditDriversName,
    [CognitoPermissionGroups.canUpdateMortgage]: canUpdateMortgage,
    [CognitoPermissionGroups.canRunBillingActions]: canRunBillingActions,
    [CognitoPermissionGroups.canRunIvansActions]: canRunIvansActions,
    [CognitoPermissionGroups.canModifyNonRatedIndividual]: canModifyNonRatedIndividual,
    [CognitoPermissionGroups.canBackfillData]: canBackfillData,
    [CognitoPermissionGroups.canCreateCompaniesInRoutable]: canCreateCompaniesInRoutable,
    [CognitoPermissionGroups.canSkipMatchingActivePolicySearch]: canSkipMatchingActivePolicySearch,
    [CognitoPermissionGroups.canUploadTaskCenterDocuments]:
      (isInternalSales || isExternalSales || isService || isTeamLeader) && !viewOnly,
    [CognitoPermissionGroups.canViewPolicyOverridesTool]: canViewPolicyOverridesTool,
    [CognitoPermissionGroups.canRevertPolicyDetails]: canRevertPolicyDetails,
    [CognitoPermissionGroups.canBulkOrderInspections]: canBulkOrderInspections,
    [CognitoPermissionGroups.canOverrideLoanNumVal]: canOverrideLoanNumVal,
    [CognitoPermissionGroups.canAllowAuxRating]: canAllowAuxRating,
    [CognitoPermissionGroups.canViewOfferOverridesTool]: canViewOfferOverridesTool,
    [CognitoPermissionGroups.canToggleHomeRepairsRequested]: canToggleHomeRepairsRequested,
    [CognitoPermissionGroups.canBulkDownloadPolicyDocuments]: canBulkDownloadPolicyDocuments,
    [CognitoPermissionGroups.canAllowWaterBackup]: canAllowWaterBackup,
    [CognitoPermissionGroups.canSeeHomeCharacteristicsDataset]: canSeeHomeCharacteristicsDataset,
    [CognitoPermissionGroups.canApproveRequestedPIPChanges]: canApproveRequestedPIPChanges,
    [CognitoPermissionGroups.canRefreshCharacteristicsAndRateOffer]: canRefreshCharacteristicsAndRateOffer,
    [CognitoPermissionGroups.canEditPriorCarrierDetails]: canEditPriorCarrierDetails,
    [CognitoPermissionGroups.canSkipOfferUnderwritingRules]: canSkipOfferUnderwritingRules,
    [CognitoPermissionGroups.canRemoveAutoViolation]: canRemoveAutoViolation
  };
}

const defaultState = {
  user: null,
  permissionGroups: [],
  isLoggedIn: false,
  updateEmail: async (emailAddress) => {
    await updateUserAttribute({
      userAttribute: {
        attributeKey: 'email',
        value: emailAddress
      }
    });
  },
  loading: true,
  userPreferences: null
};

const GET_USER_PREFERENCES = gql`
  query {
    getUserPreferences {
      username
      affinityGroups
      meiliSearchKey
      meiliSearchHost
      searchEngine
    }
  }
`;

class Auth extends React.Component {
  static contextType = MobXProviderContext; // This sets `this.context` to be the MobX store.
  static propTypes = {
    children: PropTypes.node.isRequired,
    client: PropTypes.object.isRequired,
    toast: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    Hub.listen('auth', async ({ payload }) => {
      const { event } = payload;
      if (event === 'signedIn') {
        try {
          const sessionData = await this.setupUserSession();

          if (awsConfig.trackingEnabled) {
            if (sessionData?.user?.username) {
              window.sessionStorage.setItem('branchSessionUsername', `staff-${sessionData?.user?.username}`);
            }
            track('Staff Sign In', {
              username: sessionData.user.username,
              email: sessionData.user.email
            });
          }

          const deepLink = oAuthStorage.getDeepLink();
          if (deepLink) {
            window.location.href = deepLink;
          }
        } catch (error) {
          console.error('Error handling signedIn event:', error);
        }
      }

      if (event === 'signedOut') {
        window.location.reload();
      }
    });
    this.state = defaultState;
  }

  async componentDidMount() {
    try {
      this.setState({ loading: true });
      await this.setupUserSession();
      oAuthStorage.deleteInitiatedAuthStatus();
    } catch (e) {
      console.log('Error getting session from Amplify Auth: ', e);
      this.setState({ ...defaultState, loading: false });
      oAuthStorage.deleteInitiatedAuthStatus();
    }
  }

  async getUserPreferences() {
    const { client } = this.props;
    try {
      const {
        data: { getUserPreferences }
      } = await client.query({ query: GET_USER_PREFERENCES });
      return {
        affinityGroups: getUserPreferences?.affinityGroups,
        meiliSearchKey: getUserPreferences?.meiliSearchKey,
        meiliSearchHost: getUserPreferences?.meiliSearchHost,
        searchEngine: getUserPreferences?.searchEngine
      };
    } catch {
      console.warn('Try deploying dynamoDB/staff-appsync-be to have a valid user preferences response.');
      return { affinityGroups: undefined, meiliSearchKey: undefined };
    }
  }

  // Pure function - builds complete session data with no side effects
  buildSessionData({ user, userAttributes, userPreferences }) {
    // Parse custom attributes helper
    const parseCustomAttribute = (attr) => {
      try {
        return JSON.parse(attr);
      } catch {
        return [];
      }
    };

    // Parse and clean allowed/unlicensed states
    const parsedAllowedStates = parseCustomAttribute(userAttributes['custom:allowed_states']);
    const parsedUnlicensedStates = parseCustomAttribute(userAttributes['custom:unlicensed_states']);

    const licensedStates = parsedAllowedStates?.length
      ? parsedAllowedStates.filter((state) => state?.trim() !== '')
      : [];
    const unlicensedStates = parsedUnlicensedStates?.length
      ? parsedUnlicensedStates.filter((state) => state?.trim() !== '' && licensedStates.indexOf(state) === -1)
      : [];

    return {
      // User data
      user: {
        username: user.username,
        groups: userAttributes['cognito:groups'] || [],
        email: userAttributes.email,
        firstName: userAttributes.given_name,
        lastName: userAttributes.family_name,
        phoneNumber: userAttributes.phone_number,
        affinityCode: userAttributes['custom:affinity_code'],
        affinityStems: userAttributes['custom:affinity_group_regex'],
        viewableAffinityCodes:
          userAttributes['custom:affinity_groups']?.trim()?.length > 0
            ? parseCustomAttribute(userAttributes['custom:affinity_groups'])
            : [],
        licenseNumber: userAttributes['custom:license_number'],
        agencyRole: userAttributes['custom:agency_role']
      },
      // Auth state
      isLoggedIn: true,
      loading: false,
      // Permissions (spread all permission flags)
      ...getPermissions(userAttributes),
      // States
      allowedStates: licensedStates,
      unlicensedStates,
      allStates: [...(parsedAllowedStates || []), ...(parsedUnlicensedStates || [])],
      userPreferences
    };
  }

  async setupUserSession() {
    // 1. Fetch raw auth data
    const [user, authSession, userPreferences] = await Promise.all([
      getCurrentUser(),
      fetchAuthSession(),
      this.getUserPreferences()
    ]);

    // Extract user attributes from ID token payload (includes cognito:groups)
    // What you get back from fetchUserAttributes does NOT include cognito:groups for some reason.
    const userAttributes = authSession.tokens.idToken.payload;

    // 2. Build session data (pure function)
    const sessionData = this.buildSessionData({ user, userAttributes, userPreferences });

    // 3. Update auth state
    this.context.store?.userPreferences.setUserPreferences(sessionData.userPreferences);
    this.setState(sessionData);

    // 4. Use session data for external integrations
    initializeFrontChatAndIdentify(sessionData);
    identifyUserInUpscope(sessionData.user);
    identifyUserInAppcues(sessionData);

    // 5. Analytics tracking
    await identify({
      username: sessionData.user.username,
      email: sessionData.user.email,
      isAgency: sessionData.isAgency,
      canSellAndService: sessionData.canSellAndService,
      firstName: sessionData.user.firstName || null,
      lastName: sessionData.user.lastName || null,
      agencyRole: sessionData.user.agencyRole || null,
      licensedStates: sessionData.allowedStates?.join(',') || null,
      unlicensedStates: sessionData.unlicensedStates?.join(',') || null,
      viewableAffinityCodes: sessionData.user.viewableAffinityCodes.join(',') || null
    });

    return sessionData;
  }

  render() {
    const { children } = this.props;

    return (
      <DescopeProvider
        projectId={import.meta.env.VITE_STAFF_DESCOPE_PROJECT_ID}
        {...(isProductionEnv && {
          baseUrl: import.meta.env.VITE_STAFF_DESCOPE_BASE_URL,
          sessionTokenViaCookie: true
        })}
      >
        <AuthContext.Provider value={this.state}>{children}</AuthContext.Provider>
      </DescopeProvider>
    );
  }
}

export default withToast(Auth);
